/**
 * إدارات الموارد البشرية العشر.
 *
 * الهيكلُ الذي تقوم عليه وحدةُ الموارد البشرية: عشرُ إداراتٍ تحت مدير
 * الموارد البشرية، لكلٍّ منها مسؤوليّاتُها وشاشاتُها.
 *
 * ## ولماذا تُكتب الجاهزيّة هنا
 *
 * الشاشةُ الرئيسيّةُ تقرأ هذا الملفَّ لتقول ما بُني وما لم يُبنَ. وبديلُه
 * أن تُعرض الإداراتُ العشرُ كلُّها بأبوابٍ متشابهةٍ فيفتح المستخدمُ بابًا
 * خلفه فراغ — وذاك أسوأ من ألّا يُعرض.
 *
 * والحالةُ تُحدَّث مع كلّ بناء: `PLANNED` تصير `PARTIAL` تصير `READY`،
 * ومن ينسى تحديثَها يجعل الملفَّ يكذب.
 */
import type { StatusKind } from './common.js';

/* ==========================================================================
   الجاهزية
   ========================================================================== */

export const PILLAR_STATES = ['READY', 'PARTIAL', 'PLANNED'] as const;
export type PillarState = (typeof PILLAR_STATES)[number];

export interface PillarStateDef {
  key: PillarState;
  label: string;
  kind: StatusKind;
  description: string;
}

export const PILLAR_STATE_DEFS: readonly PillarStateDef[] = [
  {
    key: 'READY',
    label: 'يعمل',
    kind: 'success',
    description: 'مبنيٌّ ومختبَرٌ ويُستعمل.',
  },
  {
    key: 'PARTIAL',
    label: 'جزئيّ',
    kind: 'warning',
    description: 'بعضُه يعمل وبعضُه لم يُبنَ — والناقصُ مكتوبٌ في الشاشة.',
  },
  {
    key: 'PLANNED',
    label: 'لم يُبنَ',
    kind: 'neutral',
    description: 'مخطَّطٌ ولم تُكتب شيفرتُه بعد.',
  },
] as const;

export const PILLAR_STATE_MAP = Object.fromEntries(
  PILLAR_STATE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<PillarState, PillarStateDef>>;

/* ==========================================================================
   الإدارات
   ========================================================================== */

export interface PillarDuty {
  label: string;
  /** هل هذه المسؤوليّةُ بعينها تعمل في النظام اليوم. */
  done: boolean;
  /** الشاشةُ التي تؤدّيها، إن وُجدت. */
  path?: string;
}

export interface HrPillar {
  key: string;
  label: string;
  /** الاسم الإنجليزيّ كما يُتداول في السوق — يبحث به كثيرون. */
  labelEn: string;
  icon: string;
  /** جملةٌ واحدةٌ تقول ما هذه الإدارة. */
  purpose: string;
  state: PillarState;
  /** البابُ الذي يُفتح منها، إن كان لها باب. */
  path: string | null;
  duties: readonly PillarDuty[];
}

export const HR_PILLARS: readonly HrPillar[] = [
  {
    key: 'acquisition',
    label: 'التوظيف والاستقطاب',
    labelEn: 'Talent Acquisition',
    icon: 'funnel',
    purpose: 'من نحتاج، وكيف نجده، وكيف يصير موظّفًا عندنا.',
    state: 'READY',
    path: '/hr/recruit',
    duties: [
      {
        label: 'تحديد الاحتياجات الوظيفية',
        done: true,
        path: '/hr/org/planning',
      },
      { label: 'ملفّ الوظيفة', done: true, path: '/hr/recruit/jobs' },
      { label: 'استمارات التقديم ومنشور التعيين', done: true, path: '/hr/recruit/forms' },
      { label: 'حملات التعيين وقنواتها', done: true, path: '/hr/recruit/campaigns' },
      { label: 'طلبات التقديم', done: true, path: '/hr/recruit/applications' },
      { label: 'المقابلات والاختبارات', done: true, path: '/hr/recruit/agenda' },
      { label: 'المسار حتى عرض العمل', done: true, path: '/hr/recruit/pipeline' },
      { label: 'العلامة كصاحب عمل', done: true, path: '/hr/recruit/jobs' },
    ],
  },
  {
    key: 'personnel',
    label: 'شؤون العاملين',
    labelEn: 'Personnel / HR Operations',
    icon: 'folder',
    purpose: 'الجانب الإداريّ اليوميّ: الملفّ والعقد والحضور والإجازة.',
    state: 'READY',
    path: '/hr/employees',
    duties: [
      {
        label: 'ملفات المرشّحين للتعيين',
        done: true,
        path: '/hr/employees/pending',
      },
      { label: 'ملفات الموظفين العاملين', done: true, path: '/hr/employees' },
      { label: 'عقود العمل', done: true, path: '/hr/contracts' },
      { label: 'الحضور والانصراف', done: true, path: '/hr/attendance' },
      { label: 'الإجازات', done: true, path: '/hr/leaves' },
      { label: 'الطلبات والأذونات', done: true, path: '/hr/requests' },
      { label: 'التأمينات', done: true, path: '/settings/payroll-rules' },
      { label: 'الاستقالات وإنهاء الخدمة', done: true, path: '/hr/employees' },
      {
        label: 'المستندات وأوراق الموظّف',
        done: true,
        path: '/hr/documents',
      },
    ],
  },
  {
    key: 'payroll',
    label: 'الرواتب والمزايا',
    labelEn: 'Payroll & Compensation & Benefits',
    icon: 'wallet',
    purpose: 'ما يقبضه الموظّف: الراتب والبدلات والخصومات والمزايا.',
    state: 'PARTIAL',
    path: '/hr/payroll',
    duties: [
      { label: 'حساب الرواتب', done: true, path: '/hr/payroll' },
      { label: 'الخصومات والحوافز', done: true, path: '/hr/payroll' },
      { label: 'ضريبة الدخل والتأمينات', done: true, path: '/settings/payroll-rules' },
      { label: 'هيكل الرواتب والدرجات', done: true, path: '/hr/org/grades' },
      { label: 'البدلات الثابتة', done: true, path: '/hr/benefits' },
      { label: 'التأمين الطبي', done: true, path: '/hr/benefits' },
      /*
       * ومقارنةُ السوق تحتاج بياناتِ سوقٍ لا يملكها هذا النظام.
       *
       * ما يملكه مقارنةُ الراتب بنطاق درجته — وهي في تخطيط القوى العاملة.
       * وعرضُ «أنت أقلُّ من السوق بعشرة بالمئة» من رقمٍ مخترَعٍ أسوأُ من
       * ألّا يُعرض: يُبنى عليه قرارُ علاوةٍ لا سندَ له.
       */
      { label: 'مقارنة الرواتب بالسوق', done: false },
    ],
  },
  {
    key: 'learning',
    label: 'التدريب والتطوير',
    labelEn: 'Learning & Development',
    icon: 'book',
    purpose: 'ما يتعلّمه الفريق، ومن يحتاج ماذا، وهل أثمر التدريب.',
    state: 'READY',
    path: '/hr/learning',
    duties: [
      {
        label: 'تحديد الاحتياجات التدريبية',
        done: true,
        path: '/hr/learning',
      },
      { label: 'الخطة التدريبية', done: true, path: '/hr/learning' },
      { label: 'إدارة الدورات وانعقاداتها', done: true, path: '/hr/learning' },
      { label: 'برامج القيادة', done: true, path: '/hr/learning' },
      { label: 'قياس أثر التدريب وتكلفته', done: true, path: '/hr/learning' },
    ],
  },
  {
    key: 'performance',
    label: 'إدارة الأداء',
    labelEn: 'Performance Management',
    icon: 'chartBar',
    purpose: 'أهدافٌ تُحدَّد، وأداءٌ يُقاس، وقرارٌ يُبنى عليه.',
    state: 'PARTIAL',
    path: '/hr/performance',
    duties: [
      { label: 'معايير الأداء وأوزانها', done: true, path: '/hr/performance' },
      { label: 'دورات التقييم', done: true, path: '/hr/performance' },
      { label: 'تحديد الأهداف', done: true, path: '/hr/performance' },
      { label: 'خطط تحسين الأداء', done: true, path: '/hr/performance' },
      /*
       * التوصيةُ تُكتب ويقرؤها من يملك القرار، ثمّ يفعلها في بابها:
       * الترقيةُ تغييرُ وظيفةٍ ودرجةٍ في الملفّ، والعلاوةُ تعديلُ الأجر.
       * وربطٌ آليٌّ يعني أن يرفع نصٌّ في خانةٍ راتبَ رجلٍ بلا أن يقرّه أحد.
       */
      { label: 'ربط الأداء بالترقية والمكافأة', done: false },
    ],
  },
  {
    key: 'org',
    label: 'التطوير التنظيمي',
    labelEn: 'Organizational Development',
    icon: 'layers',
    purpose: 'شكلُ الشركة: من يتبع من، وما وصفُ كلّ وظيفة، وإلى أين يترقّى.',
    state: 'READY',
    path: '/hr/org',
    duties: [
      { label: 'الهيكل التنظيمي', done: true, path: '/hr/setup/structure' },
      { label: 'إعداد المؤسسة وخطط العمل', done: true, path: '/hr/setup/company' },
      { label: 'الوصف الوظيفي', done: true, path: '/hr/org/positions' },
      { label: 'درجات الوظائف', done: true, path: '/hr/org/grades' },
      { label: 'المسارات المهنية', done: true, path: '/hr/org/positions' },
      { label: 'خطط الإحلال', done: true, path: '/hr/org/succession' },
      { label: 'تخطيط القوى العاملة', done: true, path: '/hr/org/planning' },
    ],
  },
  {
    key: 'relations',
    label: 'علاقات الموظفين',
    labelEn: 'Employee Relations',
    icon: 'chat',
    purpose: 'ما بين الموظّف والشركة: شكوى تُرفع، ونزاعٌ يُحلّ، ورضًا يُقاس.',
    state: 'READY',
    path: '/hr/relations',
    duties: [
      { label: 'الشكاوى والتظلّمات', done: true, path: '/hr/relations' },
      { label: 'حلّ النزاعات', done: true, path: '/hr/relations' },
      { label: 'التحقيقات وسجلّها', done: true, path: '/hr/relations' },
      { label: 'الإجراءات التأديبية', done: true, path: '/hr/relations' },
      { label: 'قياس رضا الموظفين', done: true, path: '/hr/relations' },
    ],
  },
  {
    key: 'hrbp',
    label: 'شركاء الموارد البشرية',
    labelEn: 'HR Business Partners',
    icon: 'arrowRightLeft',
    purpose: 'مسؤولُ موارد بشريةٍ لكلّ إدارة، يترجم احتياجَها إلى عمل.',
    state: 'READY',
    path: '/hr/talent',
    duties: [
      { label: 'شريكٌ لكلّ وحدةٍ وتغطيتُها', done: true, path: '/hr/talent' },
      /*
       * المتابعةُ موجزٌ يُجمع من الأبواب كلِّها: من فيها، وشواغرُها،
       * واحتياجاتُها التدريبيّة، وحالاتُها المفتوحة.
       */
      { label: 'متابعة احتياجات الوحدات', done: true, path: '/hr/talent' },
    ],
  },
  {
    key: 'talent',
    label: 'إدارة المواهب',
    labelEn: 'Talent Management',
    icon: 'star',
    purpose: 'من يُحتفظ به ويُستثمر فيه، ومن يخلف من.',
    state: 'READY',
    path: '/hr/talent',
    duties: [
      { label: 'أصحاب الإمكانات العالية', done: true, path: '/hr/talent' },
      { label: 'خطط الإحلال', done: true, path: '/hr/org/succession' },
      /* مسارُ برامج القيادة في التدريب، ويُقرأ في موجز كلّ اسمٍ هنا. */
      { label: 'تطوير القيادات', done: true, path: '/hr/learning' },
      { label: 'مراجعات المواهب بمحورين', done: true, path: '/hr/talent' },
    ],
  },
  {
    key: 'analytics',
    label: 'تحليلات الموارد البشرية',
    labelEn: 'People Analytics',
    icon: 'chart',
    purpose: 'أرقامُ القسم: دورانُ الموظّفين، وزمنُ التوظيف، والغياب.',
    state: 'READY',
    path: '/hr/analytics',
    duties: [
      { label: 'معدّل دوران الموظفين', done: true, path: '/hr/analytics' },
      { label: 'زمن التوظيف', done: true, path: '/hr/analytics' },
      { label: 'معدّل الغياب', done: true, path: '/hr/analytics' },
      { label: 'لوحات ومؤشّرات للإدارة', done: true, path: '/hr/analytics' },
      /*
       * التكلفةُ تُقاس من ميزانيّات الحملات وحدَها — وهي أرضيّةٌ لا رقمٌ
       * كامل: لا وقتَ من يوظّف، ولا أتعابَ مكتبٍ خارج حملة، ولا مكافأةَ
       * ترشيح. وحدُّها يُقال تحت الرقم في الشاشة.
       */
      { label: 'تكلفة التوظيف', done: true, path: '/hr/analytics' },
    ],
  },
] as const;

export const HR_PILLAR_MAP = Object.fromEntries(
  HR_PILLARS.map((p) => [p.key, p]),
) as Readonly<Record<string, HrPillar>>;

/**
 * نسبةُ ما بُني من مسؤوليّات إدارةٍ ما.
 *
 * تُحسب من المسؤوليّات لا تُكتب يدًا: رقمٌ يُكتب يدًا يتخلّف عن الشيفرة
 * بعد أوّل بناء، ويبقى يقول «ثمانون بالمئة» بعد أن صارت مئة.
 */
export function pillarProgress(pillar: HrPillar): {
  done: number;
  total: number;
  percent: number;
} {
  const total = pillar.duties.length;
  const done = pillar.duties.filter((d) => d.done).length;
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
  };
}
